import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useBids from '@bidding/bidding-flow/hooks/useBids';
import useVisibleBidsCount from '@bidding/bidding-flow/hooks/useVisibleBidsCount';
import {PATH_BIDDING, PATH_PAYMENT} from '@bidding/shared/constants/wizardPaths';

// "Finish your order" return-nudge banner. Shown across the authenticated
// surface (mounted inside Header) as a one-click shortcut back into an
// abandoned bidding session — whole point is to discourage starting a
// duplicate new order while bids still wait on the prior one.
//
// Live counts: `useBids` fetches the bids list for the pending order so
// the banner shows BOTH total-bidding writers and the real-time online
// count (same "{online}/{total}" shape as the chat dock header). Guarded
// against missing `bidsReducer` — selectors return safe defaults so the
// banner degrades to "empty" copy on bundles that don't ship the
// reducer, never crashes.
//
// PendingOrderNudge route-guard (/bidding, /payment, /account excluded)
// means in practice this renders on /order and sibling auth pages —
// both of which live in bundles that do have the reducer. The guard
// below is defense-in-depth for any future mount point.
//
// Dismissal: the nudge reappears on every navigation within the session,
// BUT an explicit × close is permanent for that order — `onDismiss` (from
// PendingOrderNudge) persists it so the banner stays gone across later
// navigations and page reloads until a new pending order exists.
export default function FinishOrderBanner({ order, bidsCount: fallbackCount, pathname, onDismiss }) {
    const uniqueNumber = order?.unique_number || order?.uniqueNumber;
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(false);

    // Guard against missing bidsReducer — `state.bids` undefined in
    // bundles that don't register it. Default shape keeps selectors
    // safe and useBids' dispatches silently no-op if the reducer isn't
    // wired.
    const hasBidsStore = useSelector(state => state.bids !== undefined);
    useBids(hasBidsStore ? uniqueNumber : undefined);
    const bids = useSelector(state => state.bids?.list || []);
    const liveCount = useVisibleBidsCount(bids);

    // Prefer the live count once bids are loaded; static server count is
    // the fallback while fetching (or when bidsReducer isn't available).
    const total = bids.length > 0 ? liveCount : (fallbackCount ?? 0);
    // Online count ticks live via bids list — same "chat is alive"
    // signal the ChatDock header shows as `{online}/{total}`.
    const onlineCount = bids.filter(b => b.writer?.is_online).length;

    // Re-open on every navigation (pathname change) as well as on order
    // change — so a per-page dismiss doesn't carry over to the next page.
    useEffect(() => {
        if (!uniqueNumber) {
            setOpen(false);
            return;
        }
        setOpen(true);
        const t = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(t);
    }, [uniqueNumber, pathname]);

    if (!open || !uniqueNumber) return null;

    const onClose = () => {
        setVisible(false);
        // Wait the fade-out, then unmount so the transition plays, and tell
        // the parent to persist the dismissal (won't come back on reload).
        setTimeout(() => {
            setOpen(false);
            onDismiss?.();
        }, 220);
    };

    // A writer already hired (bid accepted) but unpaid → push straight to
    // payment; otherwise the customer still needs to pick a writer.
    const biddingId = order?.bidding_id;
    const ctaHref = biddingId
        ? `${PATH_PAYMENT}?biddingId=${biddingId}&uniqueNumber=${uniqueNumber}`
        : `${PATH_BIDDING}?uniqueNumber=${uniqueNumber}`;
    const ctaLabel = biddingId ? 'Complete payment' : 'Hire a writer';

    return (
        <div
            className={`bidding-finish-order${visible ? ' is-visible' : ''}`}
            role="status"
            aria-live="polite"
        >
            <div className="bidding-finish-order__body">
                <div className="bidding-finish-order__title-row">
                    <div className="bidding-finish-order__title">Finish your order</div>
                    <span className="bidding-finish-order__id" aria-label={`Order ${uniqueNumber}`}>
                        #{uniqueNumber}
                    </span>
                </div>
                <div className="bidding-finish-order__sub">
                    <span className="bidding-finish-order__status">
                        <span
                            className="bidding-finish-order__status-dot"
                            aria-hidden="true"
                        />
                        Your order is live
                    </span>
                    {total > 0 ? (
                        <>
                            <span className="bidding-finish-order__sep" aria-hidden="true">·</span>
                            <span className="bidding-finish-order__count">
                                <span className="bid-count">{total}</span>
                                {' '}
                                {total === 1 ? 'writer' : 'writers'} bidding
                            </span>
                            {onlineCount > 0 && (
                                <>
                                    <span className="bidding-finish-order__sep" aria-hidden="true">·</span>
                                    <span className="bidding-finish-order__online">
                                        {onlineCount} online now
                                    </span>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            {' · first bids on the way'}
                        </>
                    )}
                </div>
            </div>
            <a
                className="bidding-finish-order__cta"
                href={ctaHref}
            >
                {ctaLabel}
                <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="9 6 15 12 9 18" />
                </svg>
            </a>
            <button
                type="button"
                className="bidding-finish-order__close"
                aria-label="Dismiss"
                onClick={onClose}
            >
                <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M18 6L6 18M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}
